// Pantalla principal del alumno: calendario mensual con sus reservas + cuota + reservar nueva.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/atoms/Button';
import { Icon } from '@/components/atoms/Icon';
import { Spinner } from '@/components/atoms/Spinner';
import { Calendar, type DayDecorator } from '@/components/molecules/Calendar';
import { QuotaProgress } from '@/components/molecules/QuotaProgress';
import { Header } from '@/components/organisms/Header';
import { BookingDrawer } from '@/components/organisms/BookingDrawer';
import { DayBookingsDialog } from '@/components/organisms/DayBookingsDialog';
import { ApiError } from '@/services/api';
import { cancelBooking, leaveGroupBooking, listMyBookings, type BookingListItem } from '@/services/bookings';
import { getMyQuota, type QuotaResponse } from '@/services/quota';
import styles from './ReservasAlumno.module.scss';

function isoDate(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function ReservasAlumno() {
  const [month] = useState(() => new Date());
  const [quota, setQuota] = useState<QuotaResponse | null>(null);
  const [bookings, setBookings] = useState<BookingListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [pickedDay, setPickedDay] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [q, b] = await Promise.all([
        getMyQuota(),
        listMyBookings(month.getFullYear(), month.getMonth() + 1, false),
      ]);
      setQuota(q);
      setBookings(b.items);
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Error al cargar datos';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => { void reload(); }, [reload]);

  const decorators = useMemo(() => {
    const map: Record<string, DayDecorator> = {};
    for (const b of bookings) {
      if (b.status !== 'scheduled') continue;
      const day = isoDate(new Date(b.scheduled_at));
      const acc = map[day] ?? {};
      if (b.type === 'one') acc.individual = (acc.individual ?? 0) + 1;
      else acc.group = (acc.group ?? 0) + 1;
      map[day] = acc;
    }
    return map;
  }, [bookings]);

  const dayBookings = useMemo(() => {
    if (!pickedDay) return [];
    return bookings.filter((b) => isoDate(new Date(b.scheduled_at)) === pickedDay);
  }, [bookings, pickedDay]);

  async function handleCancel(id: number) {
    try {
      await cancelBooking(id, 'cancelado por alumno');
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al cancelar');
    }
  }

  async function handleLeave(id: number) {
    try {
      await leaveGroupBooking(id);
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al salir');
    }
  }

  const hasOne = (quota?.license?.monthly_sessions_one ?? 0) > 0;
  const hasGr = (quota?.license?.monthly_sessions_gr ?? 0) > 0;

  return (
    <>
      <Header
        title="Mis reservas"
        actions={
          <Button variant="primary" onClick={() => setDrawerOpen(true)} disabled={!quota?.assigned_teacher_id}>
            <Icon name="plus" size={16} /> Reservar nueva clase
          </Button>
        }
      />

      <main className={styles.main}>
        {loading && !quota ? (
          <div className={styles.center}><Spinner size="lg" /></div>
        ) : error && !quota ? (
          <p className={styles.error}>{error}</p>
        ) : !quota ? null : (
          <>
            {!quota.assigned_teacher_id && (
              <p className={styles.warning}>
                No tienes profesor asignado. Contacta con el equipo de Knooowy para activar tus reservas.
              </p>
            )}

            <section className={styles.quotaRow}>
              {hasOne && (
                <QuotaProgress
                  label="Clases individuales este mes"
                  used={quota.consumed.one}
                  total={quota.license!.monthly_sessions_one}
                  tone="individual"
                />
              )}
              {hasGr && (
                <QuotaProgress
                  label="Clases grupales este mes"
                  used={quota.consumed.gr}
                  total={quota.license!.monthly_sessions_gr}
                  tone="group"
                />
              )}
            </section>

            <Calendar
              month={month}
              decorators={decorators}
              onDayClick={(d) => setPickedDay(d)}
              canGoPrev={false}
              canGoNext={false}
            />

            <div className={styles.legend}>
              <span className={styles.legendItem}>
                <span className={`${styles.dot} ${styles.dotIndividual}`} aria-hidden="true" />
                Individual
              </span>
              <span className={styles.legendItem}>
                <span className={`${styles.dot} ${styles.dotGroup}`} aria-hidden="true" />
                Grupal
              </span>
              <span className={styles.legendNote}>Solo se ven y reservan clases del mes en curso.</span>
            </div>

            {error && <p className={styles.error}>{error}</p>}
          </>
        )}
      </main>

      {quota && (
        <BookingDrawer
          open={drawerOpen}
          onOpenChange={setDrawerOpen}
          quota={quota}
          onBooked={() => { setDrawerOpen(false); void reload(); }}
        />
      )}

      <DayBookingsDialog
        open={pickedDay !== null}
        onOpenChange={(o) => !o && setPickedDay(null)}
        day={pickedDay}
        bookings={dayBookings}
        onCancel={handleCancel}
        onLeave={handleLeave}
      />
    </>
  );
}
